import { useMemo, useState, useEffect, useRef } from "react";
import { useDataProvider } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import { MediaTemplateResponse } from "types/";
import {
  MediaGenerationSettingsResponse,
  PresenterProfileResponse,
  Slide,
} from "pages/media/types/";
import {
  TemplateState,
  Templates,
} from "pages/media/components/storyboardEditor/types";
import { mapServerTemplateSlidesToTemplates } from "pages/media/components/storyboardEditor/helpers/mapTemplateSlidesToTemplateState";
import { useBrandKit } from "hooks/useBrandKit";
import { usePublicMediaAssets } from "hooks/usePublicMediaAssets";
import { loadImageRatio } from "pages/media/components/storyboardEditor/helpers/";
import { extractTemplatesFromMediaSettings } from "pages/media/components/storyboardEditor/hooks/useTemplates";
import { DEFAULT_MEDIA_VIDEO_ASPECT_RATIO } from "pages/media/constants/";
import { calculateMediaAspectRatio } from "pages/media/components/storyboardEditor/helpers";

type UseStreamingTemplatesParams = {
  organizationId?: string;
  mediaSettings?: MediaGenerationSettingsResponse;
  brandKitData: ReturnType<typeof useBrandKit>;
  slides: Slide[];
  presenter?: PresenterProfileResponse;
  isPresenterLoading?: boolean;
};

type UseStreamingTemplatesReturn = {
  templates: Templates;
  slideNameToTemplateIdMap: Record<string, string>;
  mediaAspectRatio: number;
  isLoading: boolean;
};

/**
 * Hook for progressively loading templates in the slides-streaming page.
 * This is a simplified version of useTemplates that:
 * - Does NOT use zustand store (read-only, no editing functionality)
 * - Progressively fetches templates as they appear in media settings
 * - Maps Flash project templates to TemplateState
 */
export const useStreamingTemplates = ({
  organizationId,
  mediaSettings,
  brandKitData,
  slides,
  presenter,
  isPresenterLoading = false,
}: UseStreamingTemplatesParams): UseStreamingTemplatesReturn => {
  const dataProvider = useDataProvider();

  const { defaultPublicFont } = usePublicMediaAssets();

  const [presenterRatio, setPresenterRatio] = useState<number>();
  const [logoRatio, setLogoRatio] = useState<number>();

  // Calculate presenter ratio
  const presenterPath = useMemo(
    () =>
      presenter?.face_profile?.extracted_asset_path ??
      presenter?.face_profile?.custom_asset_path ??
      undefined,
    [
      presenter?.face_profile?.extracted_asset_path,
      presenter?.face_profile?.custom_asset_path,
    ]
  );

  useEffect(() => {
    const getImageRatio = async (path: string) => await loadImageRatio(path);

    if (presenterPath && !presenterRatio && !isPresenterLoading) {
      getImageRatio(presenterPath).then(setPresenterRatio).catch(console.error);
    }
    if (
      brandKitData.logo?.path &&
      !logoRatio &&
      !brandKitData.isBrandKitLoading
    ) {
      getImageRatio(brandKitData.logo.path)
        .then(setLogoRatio)
        .catch(console.error);
    }
  }, [
    brandKitData.logo?.path,
    logoRatio,
    presenterPath,
    presenterRatio,
    isPresenterLoading,
    brandKitData.isBrandKitLoading,
  ]);

  // Extract template IDs from media settings
  const templateIdsAndTypes = useMemo(
    () => extractTemplatesFromMediaSettings(mediaSettings),
    [mediaSettings]
  );
  const templateIds = useMemo(
    () => Object.keys(templateIdsAndTypes),
    [templateIdsAndTypes]
  );

  // Calculate aspect ratio
  const mediaAspectRatio = useMemo(() => {
    if (mediaSettings?.width && mediaSettings.height) {
      return calculateMediaAspectRatio(
        mediaSettings.width / mediaSettings.height
      );
    }
    return DEFAULT_MEDIA_VIDEO_ASPECT_RATIO;
  }, [mediaSettings]);

  // Fetch templates
  const resource = `media/${organizationId}/templates`;
  const {
    data: templateData,
    isFetching,
    isLoading: isQueryLoading,
  } = useQuery({
    queryKey: ["streaming-templates", resource, templateIds],
    queryFn: async () => {
      if (templateIds.length === 0) return [];
      const results = await Promise.all(
        templateIds.map((id) =>
          dataProvider().getOne<MediaTemplateResponse>({
            resource,
            id,
          })
        )
      );
      return results.map((result) => result.data);
    },
    enabled: !!organizationId && templateIds.length > 0,
    staleTime: Infinity,
    keepPreviousData: true,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Check if we should map templates
  const shouldMapTemplates =
    organizationId !== undefined &&
    !brandKitData.isBrandKitLoading &&
    !isPresenterLoading &&
    templateData &&
    templateData.length > 0;

  // Keep last valid templates to prevent content collapse during transient loading states
  const lastValidTemplatesRef = useRef<Templates>(new Map<string, TemplateState>());
  const lastValidSlideNameMapRef = useRef<Record<string, string>>({});

  // Map templates to TemplateState
  const { templates, slideNameToTemplateIdMap } = useMemo(() => {
    if (!shouldMapTemplates) {
      // Return last valid templates instead of empty map during transient loading
      return {
        // eslint-disable-next-line react-hooks/refs
        templates: lastValidTemplatesRef.current,
        // eslint-disable-next-line react-hooks/refs
        slideNameToTemplateIdMap: lastValidSlideNameMapRef.current,
      };
    }

    const commonAssets = {
      defaultPublicFont,
      brandKitData,
      initialPresenter: presenter,
      contact: undefined,
      presenterRatio,
      logoRatio,
    };

    // Build slide name to template ID map
    const slideNameMap = templateData.reduce<Record<string, string>>(
      (acc, template) => {
        template.template.slides?.forEach((slide) => {
          if (slide.name) {
            acc[slide.name] = template.id;
          }
        });
        return acc;
      },
      {}
    );

    // Map templates - Flash projects use slide-based mapping
    const mappedTemplates = mapServerTemplateSlidesToTemplates(
      templateData,
      slides,
      mediaAspectRatio,
      commonAssets
    );

    // Save as last valid
    // eslint-disable-next-line react-hooks/refs
    lastValidTemplatesRef.current = mappedTemplates;
    // eslint-disable-next-line react-hooks/refs
    lastValidSlideNameMapRef.current = slideNameMap;

    return {
      templates: mappedTemplates,
      slideNameToTemplateIdMap: slideNameMap,
    };
  }, [
    shouldMapTemplates,
    templateData,
    slides,
    mediaAspectRatio,
    defaultPublicFont,
    brandKitData,
    presenter,
    presenterRatio,
    logoRatio,
  ]);

  const isLoading =
    isFetching ||
    isQueryLoading ||
    brandKitData.isBrandKitLoading ||
    isPresenterLoading ||
    // eslint-disable-next-line react-hooks/refs
    (templateIds.length > 0 && templates.size === 0);

  /* eslint-disable react-hooks/refs */
  return {
    templates,
    slideNameToTemplateIdMap,
    mediaAspectRatio,
    isLoading,
  };
  /* eslint-enable react-hooks/refs */
};
